import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { TaskSource } from './dailyApi';
import type { TaskPriority } from './tasksApi';
import type { TaskStatusCategory } from './taskStatusesApi';

/**
 * How the task looks right now, as the shared Linear-style row needs it —
 * distinct from the in-week `status` bucket the weekly report groups by.
 */
export interface WeeklyTaskPresentation {
  priorityLevel: TaskPriority;
  dueDate: string | null;
  statusLabel: string;
  statusColor: string;
  statusCategory: TaskStatusCategory;
}

// --- Types (mirror backend src/modules/weekly/weekly.types.ts) ------------

export type WeeklyStatus =
  | 'DONE'
  | 'IN_PROGRESS'
  | 'IN_REVIEW'
  | 'QA'
  | 'TODO'
  | 'BLOCKED'
  | 'BACKLOG'
  | 'CANCELLED';

export interface WeeklyStatusChange {
  at: string;
  from: WeeklyStatus | null;
  to: WeeklyStatus;
}

export interface WeeklyTask extends WeeklyTaskPresentation {
  id: string;
  identifier: string | null;
  title: string;
  url: string | null;
  // Priority: 0 none … 4 low.
  priority: number;
  projectId: string | null;
  projectName: string | null;
  assignee: { id: string; name: string } | null;
  source: TaskSource;
  status: WeeklyStatus;
  // Task creation ISO timestamp (CLE-74). May be null.
  createdAt: string | null;
  changes: WeeklyStatusChange[];
}

export interface WeeklyStatusGroup {
  status: WeeklyStatus;
  tasks: WeeklyTask[];
}

export interface WeeklyUserSummary {
  user: { id: string; name: string; email: string; role: string; avatarUrl: string | null };
  groups: WeeklyStatusGroup[];
  total: number;
}

export interface WeeklyStats {
  totalChanged: number;
  done: number;
  inProgress: number;
  inReview: number;
  qa: number;
  todo: number;
  blocked: number;
  newlyStarted: number;
}

// --- By-day timeline (CLE-74 §2) ------------------------------------------

export interface WeeklyDayChange {
  at: string;
  from: WeeklyStatus | null;
  to: WeeklyStatus;
  task: WeeklyTaskPresentation & {
    id: string;
    identifier: string | null;
    title: string;
    url: string | null;
    projectName: string | null;
    source: TaskSource;
  };
}

export interface WeeklyDayUser {
  user: { id: string; name: string; avatarUrl: string | null };
  changes: WeeklyDayChange[];
}

export interface WeeklyDay {
  date: string;
  weekday: number; // 0=Mon … 6=Sun
  users: WeeklyDayUser[];
  total: number;
}

export interface WeeklySummary {
  weekStart: string;
  weekEnd: string;
  // ISO week label for navigation / deep-linking, e.g. "2026-W24" (CLE-74 §3).
  weekParam: string;
  // True when this is the current week — disables the "next week" nav.
  isCurrentWeek: boolean;
  stats: WeeklyStats;
  users: WeeklyUserSummary[];
  days: WeeklyDay[];
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch the Weekly Summary (CLE-71 / CLE-74): for the current week (Mon–Sun, or
 * the week identified by `week`, e.g. "2026-W24"), every active member with the
 * tasks whose status changed during the week, grouped per member and per
 * status, a per-day timeline, plus header stats.
 */
export async function getWeeklySummary(week?: string): Promise<WeeklySummary> {
  const qs = week ? `?week=${encodeURIComponent(week)}` : '';
  const res = await apiFetch<Envelope<WeeklySummary>>(`/api/weekly${qs}`, {
    headers: authHeaders(),
  });
  return res.data;
}
